import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiClient, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Shield, KeyRound, Smartphone, Monitor, Download, Trash2, FileClock, AlertTriangle, Check, Users, User } from "lucide-react";
import TeamPanel from "@/components/TeamPanel";
import MyProfilePanel from "@/components/MyProfilePanel";
import MyAccessPanel from "@/components/MyAccessPanel";
import { fmtDateTime } from "@/lib/dateFormat";

export default function AccountSettingsPage() {
  const { companyLabel } = useAuth();
  return (
    <AppShell>
      <div className="mb-10">
        <div className="ntn-eyebrow mb-3" data-testid="account-eyebrow">Account &amp; security</div>
        {companyLabel && (
          <div className="text-sm text-[#4A5568] mb-1" data-testid="account-company">
            <span className="font-semibold text-[#0A192F]">{companyLabel}</span>
          </div>
        )}
        <h1 className="ntn-h1">Security Center</h1>
        <p className="ntn-body mt-2 max-w-2xl" style={{ color: "#4A5568" }}>
          Manage your password, two-factor authentication, active sessions, and personal data.
          Every change is recorded in your tamper-evident audit log.
        </p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="h-auto bg-[#F3EFEA] p-1 rounded-sm flex-wrap">
          <TabsTrigger value="profile" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-profile">
            <User className="h-3.5 w-3.5 mr-2" /> My Profile
          </TabsTrigger>
          <TabsTrigger value="access" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-access">
            <Shield className="h-3.5 w-3.5 mr-2" /> My Access
          </TabsTrigger>
          <TabsTrigger value="password" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-password">
            <KeyRound className="h-3.5 w-3.5 mr-2" /> Password
          </TabsTrigger>
          <TabsTrigger value="mfa" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-mfa">
            <Smartphone className="h-3.5 w-3.5 mr-2" /> Two-factor
          </TabsTrigger>
          <TabsTrigger value="team" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-team">
            <Users className="h-3.5 w-3.5 mr-2" /> Team
          </TabsTrigger>
          <TabsTrigger value="sessions" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-sessions">
            <Monitor className="h-3.5 w-3.5 mr-2" /> Sessions
          </TabsTrigger>
          <TabsTrigger value="audit" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-audit">
            <FileClock className="h-3.5 w-3.5 mr-2" /> Audit log
          </TabsTrigger>
          <TabsTrigger value="data" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-data">
            <Shield className="h-3.5 w-3.5 mr-2" /> Privacy
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6"><MyProfilePanel /></TabsContent>
        <TabsContent value="access" className="mt-6"><MyAccessPanel /></TabsContent>
        <TabsContent value="password" className="mt-6"><PasswordPanel /></TabsContent>
        <TabsContent value="mfa" className="mt-6"><MfaPanel /></TabsContent>
        <TabsContent value="team" className="mt-6"><TeamPanel /></TabsContent>
        <TabsContent value="sessions" className="mt-6"><SessionsPanel /></TabsContent>
        <TabsContent value="audit" className="mt-6"><AuditPanel /></TabsContent>
        <TabsContent value="data" className="mt-6"><DataPanel /></TabsContent>
      </Tabs>
    </AppShell>
  );
}

function Card({ title, description, children, action }) {
  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-8">
      <div className="flex justify-between items-start gap-6 mb-6">
        <div>
          <h2 className="ntn-h2">{title}</h2>
          {description && <p className="ntn-secondary mt-1">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function PasswordPanel() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New passwords do not match");
    setLoading(true);
    try {
      await apiClient.post("/account/change-password", {
        current_password: cur, new_password: next,
      });
      // Cookies are refreshed server-side. No localStorage token to update.
      toast.success("Password updated. Other sessions were revoked.");
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <Card title="Change password" description="Min 10 chars, with upper/lowercase, a digit and a symbol.">
      <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="password-form">
        <div><Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5568]">Current password</Label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required className="mt-2 h-11 rounded-sm" data-testid="cp-current" /></div>
        <div><Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5568]">New password</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required className="mt-2 h-11 rounded-sm" data-testid="cp-new" /></div>
        <div><Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5568]">Confirm new password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-2 h-11 rounded-sm" data-testid="cp-confirm" /></div>
        <Button disabled={loading} type="submit" className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" data-testid="cp-submit">
          {loading ? "Updating…" : "Update password"}
        </Button>
      </form>
    </Card>
  );
}

function MfaPanel() {
  const { user } = useAuth();
  return (
    <Card
      title="Multi-factor authentication"
      description="Email verification is required automatically at every sign-in for NTN staff and client users. No enrollment is needed."
      action={<span className="font-mono text-xs px-2 py-1 bg-[#22C55E]/15 text-[#15803D] rounded-sm" data-testid="mfa-status">EMAIL REQUIRED</span>}
    >
      <div className="space-y-2 max-w-lg" data-testid="mfa-email-info">
        <p className="text-sm text-[#4A5568] leading-relaxed">
          Each time you sign in, a 6-digit verification code is sent to{" "}
          <span className="font-medium text-[#0A192F]">{user?.email}</span>. The code expires
          after 10 minutes and can only be used once.
        </p>
        <p className="text-xs text-[#4A5568]">
          Authenticator-app (TOTP) enrollment has been retired. If you cannot access your
          email, contact NTN support.
        </p>
      </div>
    </Card>
  );
}

function SessionsPanel() {
  const [sessions, setSessions] = useState([]);
  const load = () => apiClient.get("/account/sessions").then(({ data }) => setSessions(data));
  useEffect(() => { load(); }, []);
  const revoke = async (fid) => {
    try { await apiClient.delete(`/account/sessions/${fid}`); toast.success("Session revoked"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card title="Active sessions" description="Revoke any session you don't recognize. Each browser/device shows up here.">
      {sessions.length === 0 ? (
        <p className="text-sm text-[#4A5568]" data-testid="sessions-empty">No active sessions.</p>
      ) : (
        <ul className="divide-y divide-[#0A192F]/10" data-testid="sessions-list">
          {sessions.map((s) => (
            <li key={s.family_id} className="py-4 flex items-center justify-between gap-4" data-testid={`session-${s.family_id}`}>
              <div className="min-w-0">
                <div className="text-sm text-[#0A192F] truncate">
                  {s.user_agent?.slice(0, 80) || "Unknown device"}
                  {s.current && <span className="ml-2 font-mono text-[10px] px-2 py-0.5 bg-[#22C55E]/15 text-[#15803D] rounded-sm">CURRENT</span>}
                </div>
                <div className="text-xs text-[#4A5568] font-mono mt-1">
                  IP {s.ip || "—"} · started {fmtDateTime(s.created_at)}
                </div>
              </div>
              {!s.current && (
                <Button variant="outline" size="sm" onClick={() => revoke(s.family_id)} className="rounded-sm" data-testid={`revoke-${s.family_id}`}>
                  Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-6 pt-5 border-t border-[#0A192F]/10 flex flex-wrap items-center justify-between gap-3" data-testid="trusted-device-section">
        <div>
          <div className="text-sm font-semibold text-[#0A192F]">Trusted device</div>
          <p className="text-xs text-[#4A5568] mt-1 max-w-md">
            If you checked "Remember this device" at sign-in, this browser skips MFA codes for 30 days.
            Forget it to require a verification code on the next login here.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm"
          onClick={async () => {
            try {
              const { data } = await apiClient.post("/auth/trusted-device/forget");
              toast.success(data.revoked ? "This device is no longer trusted" : "This device was not trusted");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
          }}
          data-testid="forget-device-btn"
        >
          Forget this device
        </Button>
      </div>
    </Card>
  );
}

function AuditPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { apiClient.get("/account/audit").then(({ data }) => setItems(data)); }, []);
  return (
    <Card title="Your audit log" description="Last 100 actions you've performed. Tamper-evident and append-only.">
      {items.length === 0 ? (
        <p className="text-sm text-[#4A5568]">No audit entries yet.</p>
      ) : (
        <ul className="divide-y divide-[#0A192F]/10" data-testid="audit-list">
          {items.map((e) => (
            <li key={e.id} className="py-3 flex items-center justify-between gap-4">
              <div>
                <div className="font-mono text-sm text-[#0A192F]">{e.action}</div>
                <div className="text-xs text-[#4A5568] font-mono mt-0.5">
                  {fmtDateTime(e.created_at)} · IP {e.ip || "—"}
                  {e.target_id && ` · ${e.target_type}=${e.target_id.slice(0, 8)}`}
                </div>
              </div>
              {e.success ? <Check className="h-4 w-4 text-[#22C55E]" /> : <AlertTriangle className="h-4 w-4 text-[#EF4444]" />}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function DataPanel() {
  const { logout } = useAuth();
  const [confirmPwd, setConfirmPwd] = useState("");
  const exportData = async () => {
    try {
      const res = await apiClient.get("/account/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "ntn-data-export.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Data export downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteAccount = async () => {
    try {
      await apiClient.delete("/account", { data: { password: confirmPwd } });
      toast.success("Account scheduled for deletion. You will be signed out.");
      await logout();
      window.location.href = "/";
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <Card title="Export your data" description="Download a JSON archive of every screening, message, file reference, and audit event tied to your account.">
        <Button onClick={exportData} className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" data-testid="export-btn">
          <Download className="h-4 w-4 mr-2" /> Download my data
        </Button>
      </Card>
      <Card title="Delete your account" description="Soft-delete now. Hard-delete in 30 days — recoverable up to that point on request.">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="rounded-sm border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/5 hover:text-[#EF4444]" data-testid="delete-account-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Delete my account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif">Delete your account?</AlertDialogTitle>
              <AlertDialogDescription>
                This will revoke all sessions, hide your data immediately, and schedule a hard delete in 30 days.
                Confirm with your password.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input type="password" placeholder="Your password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
                   className="rounded-sm h-11" data-testid="delete-confirm-password" />
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteAccount} className="bg-[#EF4444] hover:bg-[#EF4444]/90 rounded-sm" data-testid="delete-confirm-btn">
                Permanently delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </div>
  );
}
